import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { useCustomFields } from '@hooks/index';
import { connect } from 'react-redux';
import { 
    ticketTypeSelectOptionsLoadedSelector,
    ticketFormOptionsSelector,
    workgroupsOptionsSelector,
    ticketTypeFormOpenState,
    ticketTypeDetailsDrawerOpenState,
    selectedTicketDetailsSelector,
    ticketTypeFormDirtyStateSelector,
    tktCategoryNavigationStateSelector
} from '@selectors/ticketMgmtSelectors';
import { createStructuredSelector } from 'reselect';
import { Button }from '@cmsgov/design-system';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleLeft, faAngleRight, faPlus } from '@fortawesome/free-solid-svg-icons';
import { ConditionalRendering } from '@components/common/conditional-rendering';
import { TicketTypesTable } from './TicketTypesTable';
import TicketTypeForm from '../forms/TicketTypeForm';
import {
    getWorkflowTypes,
    getWorkgroups,
    getPermissions,
    openTicketTypeForm,
    openTicketDrawer,
    clearTktTypeSelectionResults,
    saveNewTicketType,
    setTicketTypeDetails,
    showTktCategoryNavigation,
} from '@actions/ticketMgmtPortal/ticketMgmtPortalActionCreators';
import {
    DEFAULT_SELECT,
    STATUSES,
    PRIORITIES,
    DEFAULT_DUE_DATE_LIMIT,
    TICKET_TYPE_FORM_NAMES,
    DEFAULT_TICKET_TYPE_FORM_FIELDS,
    DEFAULT_WORKFLOW_TYPE,
    SAVE_MODES,
} from '../forms/ticketFormConfigs';
import { configs } from '@constants/configs/configs';
import { PAGE_SIZE } from '@constants/common.constants';

const modifyCustomFieldData = (
    data: Array<{ id: any; name: any; type: any; required: any }>
): Array<{ id: any; name: any; type: any; selected: boolean; required: any }> => {
    return data.map((val) => ({
        id: val.id,
        name: val.name,
        type: val.type,
        selected: false,
        required: val.required,
    }));
};

const processSaveTicketTypePayload = (formValues, mode, category, isCategorySelected, statusList) => {
    const { id, type, filename, description, status, workgroup, expDate, permission, defaultPriority, create, open,
        complete, cancel, fileUploadEnabled, secureInbox=true, custom_fields = [], spanishName = null } = formValues;

    const payload = {
        isCategorySelected,
        statusList,
        category,
        type,
        filename,
        description,
        status,
        queueId: parseInt(workgroup),
        expDate: parseInt(expDate),
        permission,
        fileUploadEnabled,
        customFieldDetails: custom_fields,
        default_Priority: defaultPriority,
        emailConfig: JSON.stringify({
            create, open, complete, cancel, secureInbox
        }),
        // categoryEs: categoryItem?.categoryNameSpanish ?  categoryItem.categoryNameSpanish : null,
        typeEs : permission === 'TKM_WORKFLOW_PUBLIC1' ? spanishName : null
    };

    switch (mode) {
        case(SAVE_MODES.EDIT):
            return { id: id, ...payload };
        case(SAVE_MODES.ADD):
            return { ...payload, filename: DEFAULT_WORKFLOW_TYPE  };
    }
};

const TicketTypesDrawer = (props) => {
    const {
        activeCategoryIdx,
        workFlowsByCategory,
        processedList,
        onOpenRenameCategoryForm,
        optionsLoaded,
        formOptions,
        workgroupsOptions,
        onPrevClick,
        onNextClick,
        activeCategoryIndexSetter,
        statusList,
        formOpenState,
        drawerOpenState,
        openTicketTypeForm,
        openTicketDrawer,
        getWorkflowTypes,
        getWorkgroups,
        getPermissions,
        saveNewTicketType,
        clearTktTypeSelectionResults,
        selectedTicketDetails,
        setTicketTypeDetails,
        isTktTypeFormStateDirty,
        showCategoryNavigation
    } = props;
    const [ formInAddMode, setFormInAddMode ] = useState(false);
    const [ showUnsavedChangesModal, setShowUnsavedChangesModal ] = useState(false);
    const [ selectedCategory, setSelectedCategory ] = useState('');
    const { customFields=[], getCustomFields } = useCustomFields();
    const categoryItem = processedList?.[activeCategoryIdx] ?? null;
    const lastCategoryIndex = processedList?.length ? processedList?.length - 1 : -1;

    useEffect(() => {
        getCustomFields();
    },[]);
    
    useEffect(() => {
        if (!optionsLoaded) {
            getWorkflowTypes(configs.endpoints.TICKET_MGMT_WORKFLOW_TYPES);
            getWorkgroups(configs.endpoints.TICKET_MGMT_WORKGROUPS);
            getPermissions(configs.endpoints.TICKET_MGMT_PERMISSIONS);
        }
    }, [optionsLoaded]);

    useEffect(() => {
        if (!formOpenState && drawerOpenState) {
            setFormInAddMode(false);
        }
    }, [formOpenState, drawerOpenState]);

    useEffect(() => {
        const category = categoryItem?.categoryNameEnglish ?? selectedTicketDetails?.category;
        if (category) {
            setSelectedCategory(category);
        }
    }, [categoryItem?.categoryNameEnglish, selectedTicketDetails?.category]);

    const isEdit = useMemo(() => {
        return !formInAddMode && selectedTicketDetails && Object.keys(selectedTicketDetails).length > 0;
    }, [formInAddMode, selectedTicketDetails]);

    const getInitialTicketTypeFormValues = useCallback(() => ({
        [TICKET_TYPE_FORM_NAMES.NAME]: '',
        [TICKET_TYPE_FORM_NAMES.WORKFLOW]: DEFAULT_SELECT,
        [TICKET_TYPE_FORM_NAMES.DESCRIPTION]: '',
        [TICKET_TYPE_FORM_NAMES.STATUS]: DEFAULT_SELECT,
        [TICKET_TYPE_FORM_NAMES.WORKGROUP]: DEFAULT_SELECT,
        [TICKET_TYPE_FORM_NAMES.DUE_DATE]: DEFAULT_SELECT,
        [TICKET_TYPE_FORM_NAMES.PERMISSION_NAME]: DEFAULT_SELECT,
        [TICKET_TYPE_FORM_NAMES.ALLOW_UPLOAD]: false,
        [TICKET_TYPE_FORM_NAMES.PRIORITY]: DEFAULT_SELECT,
        [TICKET_TYPE_FORM_NAMES.EMAIL_CONFIG.CREATE]: true,
        [TICKET_TYPE_FORM_NAMES.EMAIL_CONFIG.OPEN]: false,
        [TICKET_TYPE_FORM_NAMES.EMAIL_CONFIG.COMPLETE]: true,
        [TICKET_TYPE_FORM_NAMES.EMAIL_CONFIG.CANCEL]: true,
        [TICKET_TYPE_FORM_NAMES.CUSTOM_FIELDS]: modifyCustomFieldData(customFields),
        [TICKET_TYPE_FORM_NAMES.SPANISH_NAME]: ''
    }), [customFields]);

    const getEditTicketTypeFormValues = useCallback(() => {
        const { id, type, typeEs, filename, description, status, workgroup, expDate, permission, defaultPriority, fileUploadEnabled,
            create=false, open=false, complete=false, cancel=false, customFields=[] } = selectedTicketDetails;
        return {
            [TICKET_TYPE_FORM_NAMES.NAME]: type,
            [TICKET_TYPE_FORM_NAMES.WORKFLOW]: filename || DEFAULT_SELECT,
            [TICKET_TYPE_FORM_NAMES.DESCRIPTION]: description,
            [TICKET_TYPE_FORM_NAMES.STATUS]: STATUSES.includes(status) ?  status : DEFAULT_SELECT,
            [TICKET_TYPE_FORM_NAMES.WORKGROUP]: workgroup && workgroup.value || DEFAULT_SELECT,
            [TICKET_TYPE_FORM_NAMES.DUE_DATE]: expDate || DEFAULT_SELECT,
            [TICKET_TYPE_FORM_NAMES.PERMISSION_NAME]: permission || DEFAULT_SELECT,
            [TICKET_TYPE_FORM_NAMES.PRIORITY]: defaultPriority || DEFAULT_SELECT,
            [TICKET_TYPE_FORM_NAMES.ALLOW_UPLOAD]: fileUploadEnabled,
            [TICKET_TYPE_FORM_NAMES.EMAIL_CONFIG.CREATE]: create,
            [TICKET_TYPE_FORM_NAMES.EMAIL_CONFIG.OPEN]: open,
            [TICKET_TYPE_FORM_NAMES.EMAIL_CONFIG.COMPLETE]: complete,
            [TICKET_TYPE_FORM_NAMES.EMAIL_CONFIG.CANCEL]: cancel,
            [TICKET_TYPE_FORM_NAMES.CUSTOM_FIELDS]: customFields,
            [TICKET_TYPE_FORM_NAMES.SPANISH_NAME]: typeEs
        };
    }, [selectedTicketDetails, customFields, optionsLoaded, formOptions, workgroupsOptions]);

    const TICKET_TYPE_FORM_FIELDS = useMemo(() => {
        return DEFAULT_TICKET_TYPE_FORM_FIELDS.map((field) => {
            switch (field.name) {
                case TICKET_TYPE_FORM_NAMES.WORKFLOW:
                    field.options = [DEFAULT_SELECT, ...(formOptions?.workflowTypes || [])];
                    break;
                case TICKET_TYPE_FORM_NAMES.WORKGROUP:
                    field.options = workgroupsOptions || [];
                    break;
                case TICKET_TYPE_FORM_NAMES.PERMISSION_NAME:
                    field.options = [DEFAULT_SELECT, ...(formOptions?.permissions || [])];
                    break;
                case TICKET_TYPE_FORM_NAMES.DUE_DATE:
                    field.options = [DEFAULT_SELECT, ...Array.from({ length: DEFAULT_DUE_DATE_LIMIT }, (_, i) => i + 1)];
                    break;
                case TICKET_TYPE_FORM_NAMES.STATUS:
                    field.options = STATUSES || [];
                    break;
                case TICKET_TYPE_FORM_NAMES.PRIORITY:
                    field.options = PRIORITIES || [];
                    break;
                default:
                    break;
            }
            return field;
        });
    }, [DEFAULT_TICKET_TYPE_FORM_FIELDS, formOptions, workgroupsOptions, STATUSES, PRIORITIES, DEFAULT_DUE_DATE_LIMIT]);

    const onOpenTicketTypeForm = () => {
        openTicketTypeForm(true);
        setFormInAddMode(true);
    };

    const handleUnsavedModalClose = () => {
        setShowUnsavedChangesModal(false);
    };

    const handleSave = useCallback((newVals) => {
        const isCategorySelected = activeCategoryIdx > -1;
        if (isEdit) {
            const { id } = selectedTicketDetails;
            saveNewTicketType(processSaveTicketTypePayload({ id, ...newVals }, SAVE_MODES.EDIT, selectedCategory, isCategorySelected, statusList));
        } else {
            saveNewTicketType(processSaveTicketTypePayload(newVals, SAVE_MODES.ADD, selectedCategory, isCategorySelected, statusList));
        }
    }, [activeCategoryIdx, isEdit, selectedTicketDetails, saveNewTicketType, statusList]);

    const handleUnsavedModalOpen = () => {
        setShowUnsavedChangesModal(true);
    };

    const shouldShowUnsavedChangesModal = useCallback(() => {
        if(isTktTypeFormStateDirty) {
            handleUnsavedModalOpen();
        } else {
            handleDrawerClose();
        }
        
    }, [isTktTypeFormStateDirty]);

    const handleDrawerClose = useCallback(() => {
        activeCategoryIndexSetter?.(-1);
        openTicketTypeForm(false);
        openTicketDrawer(false);
        clearTktTypeSelectionResults?.();
        setTicketTypeDetails(null);
        showTktCategoryNavigation(false);
        setFormInAddMode(false);
    }, [
        activeCategoryIndexSetter,
        openTicketTypeForm,
        openTicketDrawer,
        clearTktTypeSelectionResults,
        setTicketTypeDetails,
        showTktCategoryNavigation,
    ]);
    
    const tktTypeFormHeader = selectedCategory ?? '';
    const initTktTypeFormValues = isEdit ? getEditTicketTypeFormValues() : getInitialTicketTypeFormValues();
    return (
        <ConditionalRendering isTrue={drawerOpenState}>
            <div className="drawer-overlay" data-testid="drawer-overlay">
                <div className="drawer-popup" data-testid="drawer-popup">
                    <div className="drawer-header" data-testid="drawer-header">
                        <h2 title={tktTypeFormHeader} aria-label={`Ticket Type Form Header: ${tktTypeFormHeader}`} data-testid="drawer-header-title">{tktTypeFormHeader}</h2>                   
                        <div className="pagination-bar" data-testid="pagination-bar">
                            <ConditionalRendering isTrue={!formOpenState && !showCategoryNavigation}>
                                <Button data-testid="ticket-type-previous-button" className="usa-button gi-button gi-button__secondary" aria-label="Previous Category" disabled={activeCategoryIdx === 0} onClick={onPrevClick}>
                                    <FontAwesomeIcon icon={faAngleLeft} className='prev-icon' />
                                    <FormattedMessage id='common.button.label.previous' />
                                </Button>
                                <Button data-testid="ticket-type-next-button" className="usa-button gi-button gi-button__secondary margin-r-20" aria-label="Next Category" disabled={activeCategoryIdx === lastCategoryIndex} onClick={onNextClick}>
                                    <FormattedMessage id='common.button.label.next' />
                                    <FontAwesomeIcon icon={faAngleRight} className='next-icon' />
                                </Button>
                            </ConditionalRendering>
                            <ConditionalRendering isTrue={!formOpenState}>
                                <button className="close" aria-label="Close drawer" data-testid="drawer-close-button" onClick={handleDrawerClose}>✕</button>
                            </ConditionalRendering>
                            <ConditionalRendering isTrue={formOpenState}>
                                <button className="close" aria-label="Close drawer with unsaved changes" data-testid="drawer-close-unsaved-button" onClick={shouldShowUnsavedChangesModal}>✕</button>
                            </ConditionalRendering>
                        </div>
                    </div>
                    <ConditionalRendering isTrue={!formOpenState}>
                        <div className='action-bar' data-testid="action-bar">
                            <Button id='renameTicketCategory' data-testid="rename-ticket-category-button" className='usa-button gi-button gi-button__secondary' onClick={onOpenRenameCategoryForm} aria-label="Rename ticket category">
                                <FormattedMessage id="ticket_management.rename_category"/>
                            </Button>
                            <Button id='addNewTicketType' data-test-id="add-new-ticket-type" className='usa-button gi-button gi-button__primary' onClick={onOpenTicketTypeForm} aria-label="Add new ticket type">
                                <FontAwesomeIcon icon={faPlus} className='fa-add-icon' />
                                <FormattedMessage id="ticket_management.add_new_ticket_type"/>
                            </Button> 
                        </div>

                        <div className='drawer-table-holder'role="region" aria-label="Ticket Types Table" data-testid="drawer-table-holder">
                            <TicketTypesTable ticketTypesData={workFlowsByCategory} pageSize={PAGE_SIZE} showCategoryColumn={false} />
                        </div>
                    </ConditionalRendering>
                    <ConditionalRendering isTrue={formOpenState}>
                        <TicketTypeForm 
                            initialFormValues={initTktTypeFormValues}
                            formFields={TICKET_TYPE_FORM_FIELDS} 
                            onSaveHandler={handleSave} 
                            onCancelHandler={handleDrawerClose} 
                            showUnsavedModal={showUnsavedChangesModal} 
                            showModalSetter={handleUnsavedModalOpen} 
                            closeModalSetter={handleUnsavedModalClose}
                            data-testid="ticket-type-form" />
                    </ConditionalRendering>
                    
                </div>
            </div>
        </ConditionalRendering>
    );
};

const mapStateToProps = createStructuredSelector({
    optionsLoaded: ticketTypeSelectOptionsLoadedSelector,
    formOptions: ticketFormOptionsSelector,
    workgroupsOptions: workgroupsOptionsSelector,
    formOpenState: ticketTypeFormOpenState,
    drawerOpenState: ticketTypeDetailsDrawerOpenState,
    selectedTicketDetails: selectedTicketDetailsSelector,
    isTktTypeFormStateDirty: ticketTypeFormDirtyStateSelector,
    showCategoryNavigation: tktCategoryNavigationStateSelector,
});

const mapDispatchToProps = {
    getWorkflowTypes,
    getWorkgroups,
    getPermissions,
    saveNewTicketType,
    openTicketTypeForm,
    openTicketDrawer,
    clearTktTypeSelectionResults,
    setTicketTypeDetails,
    showTktCategoryNavigation,
};

export default connect(mapStateToProps, mapDispatchToProps)(TicketTypesDrawer);